import { useCallback } from "react";
import { Link } from "react-router";

import { getPlanName } from "@skilitics/designer-billing";
import { useFormatMessage, type FormatMessages, type FormatMessageValues } from "@skilitics/localization";
import type { AccessDeniedCause } from "@skilitics/nautilus-endpoint-definition";

const formatStrings = {
  // There is a limit of {quota} for {metric}, and there is no plan you can upgrade to.
  quota: {
    en: [
      "You have reached your current {metric} limit.",
      "Your current {plan} plan has a limit of {quota}{quotaSuffix}.",
      "You can resolve this by {quotaResolveAction}.",
    ].join(" "),
  },
  // There is a limit of {quota} for {metric}, and you can upgrade to {requiredPlan} to increase it.
  quotaPlan: {
    en: [
      "You have reached your current {metric} limit.",
      "Your current {plan} plan has a limit of {quota}{quotaSuffix}.",
      "You can resolve this by {quotaResolveAction}, or by {upgradeBillingPageLink}.",
    ].join(" "),
  },
  // This action is not available on your current plan, and you can upgrade to {requiredPlan} to enable it.
  plan: {
    en: "This s not available on your current {plan} plan. You can resolve this by {upgradeBillingPageLink}.",
  },
  // Your plan would allow this action, but it is not in an active state, e.g. it is unpaid.
  state: { en: "Your {plan} plan is {state}. Resolve this on {billingPageLink}." },
  // Some other reason we haven't mapped a message for yet.
  default: { en: "Access denied: {code}" },

  // Billing page link text for upgrading to a specific new plan
  upgradeLinkText: {
    en: "upgrading to a {requiredPlan} plan",
  },
  // Billing page link text otherwise
  billingLinkText: {
    en: "the billing page",
  },
} satisfies FormatMessages;

export interface FormatAccessDeniedOptions {
  // The resource being denied access to, e.g. "module" or "storage"
  metric: string;
  // The suffix to be added to the quota from the server, e.g. " MB"
  quotaSuffix?: string;
  // The action a user can take to reduce their usage of quota, e.g.
  // "deleting or archiving one of your modules", or
  // "removing large media assets like videos from your media library"
  quotaResolveAction: string;
}

export type FormatAccessDenied = (cause: AccessDeniedCause) => string;

// Move this somewhere more approprate, translations
const createModuleAccessDeniedOptions = {
  metric: "module",
  quotaResolveAction: "deleting a module",
};
export const useFormatCreateModuleAccessDenied = () => {
  return useFormatAccessDenied(createModuleAccessDeniedOptions);
};

export const useFormatAccessDenied = ({
  metric,
  quotaSuffix = "",
  quotaResolveAction,
}: FormatAccessDeniedOptions): FormatAccessDenied => {
  const formatMessage = useFormatMessage(formatStrings);
  return useCallback(
    (cause) => {
      let key: keyof typeof formatStrings;
      switch (cause.code) {
        case "quota":
          key = cause.requiredPlan ? "quotaPlan" : "quota";
          break;
        case "plan":
          key = "plan";
          break;
        case "state":
          key = "state";
          break;
        default:
          key = "default";
          break;
      }
      const values: FormatMessageValues = {
        metric,
        quotaSuffix,
        quotaResolveAction,
        quota: cause.quota,
        plan: getPlanName(cause.plan),
        requiredPlan: cause.requiredPlan ? getPlanName(cause.requiredPlan) : undefined,
        state: cause.state,
        code: cause.code,
      };
      return formatMessage(key, {
        ...values,
        // need to provide key just to avoid dumb react warning
        upgradeBillingPageLink: (
          <Link key={1} to="/settings/billing">
            {formatMessage("upgradeLinkText", values)}
          </Link>
        ),
        billingPageLink: (
          <Link key={1} to="/settings/billing">
            {formatMessage("billingLinkText", values)}
          </Link>
        ),
      });
    },
    [formatMessage, metric, quotaSuffix, quotaResolveAction],
  );
};
